import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Badge,
  useTheme as useMuiTheme,
  useMediaQuery,
  Tooltip,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
  Notifications,
  Restaurant,
  DarkMode,
  LightMode,
  Brightness4,
  Home,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useCustomTheme } from '../../contexts/ThemeContext';

const Header = ({ onSidebarToggle }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const { showSuccess } = useNotification();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    showSuccess('Logged out successfully');
  };

  const handleHomePageNavigation = () => {
    // Show confirmation dialog for public page navigation
    const confirmed = window.confirm(
      'You are about to navigate to the public website. This will log you out of the admin panel. Continue?'
    );
    
    if (!confirmed) {
      return; // User cancelled
    }
    
    // Clear authentication tokens when going to public pages
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Show success message
    showSuccess('Successfully logged out and navigating to public website');
    
    // Navigate to home page
    navigate('/');
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2, md: 3 }, minHeight: { xs: '56px', sm: '64px' } }}>
        <IconButton
          edge="start"
          onClick={onSidebarToggle}
          sx={{ mr: 2, color: 'text.primary' }}
        >
          <MenuIcon sx={{ fontSize: '1.4rem' }} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Restaurant sx={{ mr: { xs: 0.5, sm: 1 }, color: 'primary.main', fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' } }} />
          <Typography 
            variant="h6" 
            fontWeight="600" 
            color="primary.main"
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Restaurant Admin
          </Typography>
          <Typography 
            variant="h6" 
            fontWeight="600" 
            color="primary.main"
            sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              display: { xs: 'block', sm: 'none' },
              maxWidth: '80px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            Admin
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5, md: 1 } }}>
          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton 
              onClick={toggleTheme} 
              color="inherit"
              sx={{
                transition: 'transform 0.3s ease',
                p: { xs: 0.5, sm: 0.75, md: 1 },
                '&:hover': {
                  transform: 'rotate(180deg)',
                },
                '& .MuiSvgIcon-root': { fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' } }
              }}
            >
              {isDarkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <IconButton 
            color="inherit"
            sx={{ 
              p: { xs: 0.5, sm: 0.75, md: 1 },
              '& .MuiSvgIcon-root': { fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' } }
            }}
          >
            <Badge 
              badgeContent={3} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.75rem' },
                  minWidth: { xs: '14px', sm: '16px', md: '20px' },
                  height: { xs: '14px', sm: '16px', md: '20px' },
                }
              }}
            >
              <Notifications />
            </Badge>
          </IconButton>

          {/* Home Page Button */}
          <Tooltip title="Go to Public Website (will log you out)">
            <Button
              variant="outlined"
              startIcon={<Home sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' } }} />}
              onClick={handleHomePageNavigation}
              sx={{
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  bgcolor: 'warning.50',
                },
                mr: { xs: 0.25, sm: 0.5, md: 1 },
                px: { xs: 0.75, sm: 1, md: 2 },
                py: { xs: 0.5, sm: 0.75, md: 1 },
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                minWidth: 'auto',
              }}
            >
              {isMobile ? 'Home' : 'Public Website'}
            </Button>
          </Tooltip>

          {/* User Menu */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{ ml: { xs: 0.25, sm: 0.5, md: 1 }, p: { xs: 0.5, sm: 0.75, md: 1 } }}
          >
            <Avatar
              sx={{
                width: { xs: 28, sm: 32, md: 40 },
                height: { xs: 28, sm: 32, md: 40 },
                bgcolor: 'primary.main',
                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
                fontWeight: 600,
              }}
            >
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 8,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  mx: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                {user?.full_name || user?.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'Admin User'}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 1 }} />

            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <AccountCircle sx={{ fontSize: '1.4rem' }} />
              </ListItemIcon>
              Profile
            </MenuItem>

            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <Settings sx={{ fontSize: '1.4rem' }} />
              </ListItemIcon>
              Settings
            </MenuItem>

            <MenuItem onClick={(e) => e.stopPropagation()}>
              <ListItemIcon>
                <Brightness4 sx={{ fontSize: '1.4rem' }} />
              </ListItemIcon>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDarkMode}
                    onChange={toggleTheme}
                    size="small"
                  />
                }
                label={isDarkMode ? 'Dark Mode' : 'Light Mode'}
                sx={{ 
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Logout sx={{ fontSize: '1.4rem' }} color="error" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
